import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useRateLimits, type RateLimitRow } from '@/rate-limits/api'
import { Alert, AlertDescription } from '@/components/ui/alert'

function formatCap(row: RateLimitRow): string {
  if (row.cap === null || row.per === null) {
    return '—'
  }
  return `${row.cap}/${row.per}/${row.by ?? '?'}`
}

function HumanPaceSummary({ row }: { row: RateLimitRow }) {
  const { t } = useTranslation()
  const enabled = row.enabled !== false
  return (
    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
      <span>
        {t('rateLimits.humanPaceEnabled')}: <strong>{enabled ? '✓' : '✕'}</strong>
      </span>
      {row.gap_hard_ms != null && row.sleep_hard_us != null && (
        <span>
          {t('rateLimits.humanPaceHard', {
            gap: row.gap_hard_ms,
            sleep: Math.round(row.sleep_hard_us / 1000),
          })}
        </span>
      )}
      {row.gap_soft_ms != null && row.sleep_soft_us != null && (
        <span>
          {t('rateLimits.humanPaceSoft', {
            gap: row.gap_soft_ms,
            sleep: Math.round(row.sleep_soft_us / 1000),
          })}
        </span>
      )}
    </div>
  )
}

/**
 * `/rate-limits` — admin read-only view of the per-endpoint
 * rate-limit catalogue + the adaptive HumanPaceMiddleware thresholds.
 *
 * Phase 33.4 / DEC-RATE-001. Source of truth lives in the backend
 * `config/rate_limits.php`; this view renders the API response with
 * no edit-affordance (changes flow via the env / config layer).
 */
export function RateLimitList() {
  const { t } = useTranslation()
  const limits = useRateLimits()

  return (
    <main className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline self-start">
          {t('rateLimits.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('rateLimits.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('rateLimits.intro')}</p>
      </header>

      {limits.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}

      {limits.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('rateLimits.loadError')}</AlertDescription>
        </Alert>
      )}

      {!limits.isPending && !limits.isError && (limits.data?.length ?? 0) === 0 && (
        <p className="text-muted-foreground">{t('rateLimits.empty')}</p>
      )}

      {!limits.isPending && !limits.isError && (limits.data?.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-3">
          {(limits.data ?? []).map((row) => (
            <li key={row.name} className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                <span className="font-medium">{row.name}</span>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">
                  {formatCap(row)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{row.description}</p>
              {row.endpoints.length > 0 && (
                <ul className="flex flex-wrap gap-1 text-xs">
                  {row.endpoints.map((ep) => (
                    <li key={ep} className="rounded bg-muted px-2 py-0.5 font-mono">
                      {ep}
                    </li>
                  ))}
                </ul>
              )}
              {row.name === 'human-pace' && <HumanPaceSummary row={row} />}
            </li>
          ))}
        </ul>
      )}
    </main>
  )
}
